import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Stack,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  Chip,
  Avatar,
  Divider,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  FormControl,
  FormHelperText,
  Radio,
  RadioGroup,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Alert,
  Autocomplete,
  IconButton
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { DetailPageHeader, DetailTwoColumnGrid } from '../../components/common/DetailPageLayout';
import RecordAuditMeta from '../../components/common/RecordAuditMeta';
import {
  Edit as EditIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  LinkedIn as LinkedInIcon,
  TrendingUp as PromoteIcon,
  Badge as EmployeeIcon,
  Add as AddIcon,
  AddBusiness as AddBusinessIcon,
  LinkOff as UnlinkIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  DeleteOutline as DeleteOutlineIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api, { getApiBaseUrl, apiUpload } from '../../utils/api';
import { getAuthAccessToken } from '../../utils/demoPreviewStorage';
import { useAuth } from '../../context/AuthContext';
import { contactHasRole, effectiveContactRoles, contactIsContractor, TYPE_LABELS, allowedNextRoles } from './contactRolesUtil';
import useDocumentTypes, { labelForDocumentType, defaultDocumentTypeCode } from '../../hooks/useDocumentTypes';

const filterAccountPickOptions = createFilterOptions();

function documentDownloadUrl(documentId) {
  const base = String(getApiBaseUrl() || '').replace(/\/+$/, '');
  return `${base}/documents/${documentId}/download`;
}

const ContactDetails = ({ detailId: detailIdProp, embedded, onCloseEmbedded } = {}) => {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const id = detailIdProp ?? routeId;
  const { hasPermission, isAdmin } = useAuth();
  const [contact, setContact] = useState(null);
  const [workAuthOptions, setWorkAuthOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  const [addAccountOpen, setAddAccountOpen] = useState(false);
  const [accountSearch, setAccountSearch] = useState('');
  const [accountPickOptions, setAccountPickOptions] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [newAccountRelationship, setNewAccountRelationship] = useState('primary');
  const [linkingAccount, setLinkingAccount] = useState(false);
  const [quickNewAccountOpen, setQuickNewAccountOpen] = useState(false);
  const [quickNewAccount, setQuickNewAccount] = useState({
    company_name: '',
    account_type: 'client',
    industry: '',
    status: 'active',
  });
  const [creatingAccount, setCreatingAccount] = useState(false);

  const [addDocOpen, setAddDocOpen] = useState(false);
  const [docForm, setDocForm] = useState({
    title: '',
    document_type: 'other',
    file: null
  });
  const [docUploading, setDocUploading] = useState(false);
  const { documentTypes, loading: docTypesLoading } = useDocumentTypes();

  useEffect(() => {
    if (!documentTypes.length) return;
    const codes = new Set(documentTypes.map((t) => t.code));
    const def = defaultDocumentTypeCode(documentTypes);
    setDocForm((f) => (codes.has(f.document_type) ? f : { ...f, document_type: def }));
  }, [documentTypes]);

  const [duplicateAccountDialog, setDuplicateAccountDialog] = useState({
    open: false,
    existing: null,
    serverMessage: ''
  });
  const [trainingAssignments, setTrainingAssignments] = useState([]);
  const [trainingLoading, setTrainingLoading] = useState(false);
  const [benchAssignments, setBenchAssignments] = useState([]);
  const [benchAssignmentsLoading, setBenchAssignmentsLoading] = useState(false);
  const [deleteChoiceDialog, setDeleteChoiceDialog] = useState({
    open: false,
    targetType: null,
    target: null
  });
  const [deleteChoiceMode, setDeleteChoiceMode] = useState('unlink');

  const tabDefs = useMemo(() => {
    if (!contact) return [];
    const t = [
      { key: 'overview', label: 'Overview' },
      { key: 'accounts', label: 'Accounts' }
    ];
    if (contactHasRole(contact, 'candidate')) {
      t.push({ key: 'submissions', label: 'Submissions' });
      t.push({ key: 'whichBench', label: 'Which Bench' });
    }
    t.push({ key: 'documents', label: 'Documents' });
    if (contactHasRole(contact, 'trainee')) {
      t.push({ key: 'training', label: 'Training attended' });
      t.push({ key: 'exams', label: 'Exams taken' });
    }
    if (contactHasRole(contact, 'employee') && contact.Employee?.id) {
      t.push({ key: 'employee', label: 'Employee HR' });
      t.push({ key: 'okr', label: 'OKRs' });
    }
    return t;
  }, [contact]);

  useEffect(() => {
    if (tabDefs.length > 0 && activeTab >= tabDefs.length) setActiveTab(0);
  }, [tabDefs.length, activeTab]);

  useEffect(() => {
    if (!id || !contact) {
      setTrainingAssignments([]);
      return;
    }
    if (!contactHasRole(contact, 'trainee')) {
      setTrainingAssignments([]);
      return;
    }
    let cancel = false;
    setTrainingLoading(true);
    (async () => {
      try {
        const r = await api.get('/training/assignments', {
          params: { contact_id: id, limit: 100, page: 1 }
        });
        if (!cancel) setTrainingAssignments(r.data?.data || []);
      } catch {
        if (!cancel) setTrainingAssignments([]);
      } finally {
        if (!cancel) setTrainingLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [id, contact]);

  useEffect(() => {
    if (!id || !contact || !contactHasRole(contact, 'candidate')) {
      setBenchAssignments([]);
      return;
    }
    let cancelled = false;
    setBenchAssignmentsLoading(true);
    api
      .get(`/contacts/${id}/benches`)
      .then((r) => {
        if (!cancelled) {
          const d = r.data?.data || {};
          setBenchAssignments(Array.isArray(d.assignments) ? d.assignments : []);
        }
      })
      .catch(() => {
        if (!cancelled) setBenchAssignments([]);
      })
      .finally(() => {
        if (!cancelled) setBenchAssignmentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, contact]);

  useEffect(() => {
    fetchContact();
    fetchWorkAuthorizations();
  }, [id]);

  const fetchContact = async () => {
    try {
      const response = await api.get(`/contacts/${id}`);
      setContact(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch contact');
      if (embedded && typeof onCloseEmbedded === 'function') onCloseEmbedded();
      else navigate('/contacts');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkAuthorizations = async () => {
    try {
      const response = await api.get('/contacts/work-authorizations');
      setWorkAuthOptions(response.data?.data?.options || []);
    } catch (error) {
      console.error('Failed to fetch work authorization options');
      setWorkAuthOptions([]);
    }
  };

  useEffect(() => {
    if (!addAccountOpen) return;
    const handle = setTimeout(() => {
      (async () => {
        try {
          const r = await api.get('/accounts', {
            params: { limit: 40, status: 'active', search: accountSearch || undefined }
          });
          setAccountPickOptions(r.data.data || []);
        } catch (e) {
          console.error(e);
        }
      })();
    }, 300);
    return () => clearTimeout(handle);
  }, [addAccountOpen, accountSearch]);

  const linkedAccountIds = useMemo(
    () => new Set((contact?.Accounts || []).map((a) => a.id)),
    [contact]
  );
  const canAddAccounts = hasPermission('accounts_add');

  const accountAutocompleteOptions = useMemo(() => {
    const rows = accountPickOptions.filter((a) => !linkedAccountIds.has(a.id));
    if (!canAddAccounts) return rows;
    return [
      { id: '__erp4c_create_account__', __createRow: true, company_name: 'Create new account…', account_code: '' },
      ...rows,
    ];
  }, [accountPickOptions, linkedAccountIds, canAddAccounts]);

  const resetQuickNewAccountForm = () => {
    setQuickNewAccount({
      company_name: '',
      account_type: 'client',
      industry: '',
      status: 'active',
    });
  };

  const handleQuickCreateAccount = async () => {
    const name = String(quickNewAccount.company_name || '').trim();
    if (!name) {
      toast.error('Company name is required');
      return;
    }
    setCreatingAccount(true);
    try {
      const industryTrim = String(quickNewAccount.industry || '').trim();
      const r = await api.post('/accounts', {
        company_name: name,
        account_type: quickNewAccount.account_type,
        status: quickNewAccount.status || 'active',
        ...(industryTrim ? { industry: industryTrim } : {}),
      });
      if (r.status === 202 && r.data?.data?.request_submitted) {
        setDuplicateAccountDialog({
          open: true,
          existing: r.data?.data?.existing_account || null,
          serverMessage:
            r.data?.message ||
            'A company with the same name already exists. A duplicate-account request was submitted for administrator review.'
        });
        setQuickNewAccountOpen(false);
        resetQuickNewAccountForm();
        return;
      }
      const created = r.data?.data;
      if (created?.id) {
        setAccountPickOptions((prev) => {
          const row = {
            id: created.id,
            company_name: created.company_name,
            account_code: created.account_code,
          };
          return [row, ...prev.filter((a) => a.id !== created.id)];
        });
        setSelectedAccount({
          id: created.id,
          company_name: created.company_name,
          account_code: created.account_code,
        });
      }
      toast.success('Account created — it is selected below. Choose relationship if needed, then click Link account.');
      setQuickNewAccountOpen(false);
      resetQuickNewAccountForm();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not create account');
    } finally {
      setCreatingAccount(false);
    }
  };

  const handleLinkAccount = async () => {
    if (!selectedAccount) {
      toast.error('Select an account');
      return;
    }
    if (linkedAccountIds.has(selectedAccount.id)) {
      toast.error('That account is already linked');
      return;
    }
    setLinkingAccount(true);
    try {
      await api.post(`/contacts/${id}/accounts`, {
        account_id: selectedAccount.id,
        relationship_type: newAccountRelationship
      });
      toast.success('Account linked');
      setAddAccountOpen(false);
      setSelectedAccount(null);
      setAccountSearch('');
      setNewAccountRelationship('primary');
      fetchContact();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to link account');
    } finally {
      setLinkingAccount(false);
    }
  };

  const openDeleteChoice = (targetType, target) => {
    setDeleteChoiceMode('unlink');
    setDeleteChoiceDialog({ open: true, targetType, target });
  };

  const handleDocumentDownload = async (doc) => {
    try {
      const token = getAuthAccessToken();
      const response = await fetch(documentDownloadUrl(doc.id), {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) {
        toast.error('Failed to download');
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || doc.title;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      toast.error('Failed to download');
    }
  };

  const handleConfirmDeleteChoice = async () => {
    if (!deleteChoiceDialog.target || !deleteChoiceDialog.targetType) return;
    try {
      if (deleteChoiceDialog.targetType === 'account') {
        if (deleteChoiceMode === 'unlink') {
          await api.delete(`/contacts/${id}/accounts/${deleteChoiceDialog.target.id}`);
        } else {
          await api.delete(`/accounts/${deleteChoiceDialog.target.id}`);
        }
      } else if (deleteChoiceDialog.targetType === 'document') {
        await api.delete(`/documents/${deleteChoiceDialog.target.id}`, {
          params: {
            mode: deleteChoiceMode === 'unlink' ? 'unlink' : 'permanent',
            entity_type: 'contact',
            entity_id: id
          }
        });
      }
      toast.success(
        deleteChoiceMode === 'unlink'
          ? 'Link removed successfully'
          : 'Record marked for deletion (or deleted immediately based on retention setting).'
      );
      setDeleteChoiceDialog({ open: false, targetType: null, target: null });
      fetchContact();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Delete action failed');
    }
  };

  const handleAddDocument = async () => {
    if (!docForm.file) {
      toast.error('Choose a file');
      return;
    }
    setDocUploading(true);
    try {
      await apiUpload('/documents', docForm.file, {
        title: docForm.title || docForm.file.name.replace(/\.[^.]+$/, ''),
        document_type: docForm.document_type,
        entity_type: 'contact',
        entity_id: id
      });
      toast.success('Document uploaded');
      setAddDocOpen(false);
      setDocForm({ title: '', document_type: defaultDocumentTypeCode(documentTypes), file: null });
      fetchContact();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Upload failed');
    } finally {
      setDocUploading(false);
    }
  };

  const contactTypeColors = {
    regular: '#6b7280',
    candidate: '#6366f1',
    employee: '#10b981',
    contractor: '#3b82f6',
    contractor_independent: '#3b82f6',
    contractor_c2c: '#0ea5e9',
    company: '#f59e0b',
    trainee: '#8b5cf6',
    guest_signedup: '#94a3b8',
    trainer: '#d946ef'
  };

  // Promote to Employee states
  const [promoteDialog, setPromoteDialog] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [advanceDialog, setAdvanceDialog] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [advanceRole, setAdvanceRole] = useState('');
  const [promoteForm, setPromoteForm] = useState({
    department: '',
    designation: '',
    employment_type: 'fulltime',
    create_user_account: true,
    temporary_password: '',
    reporting_manager_contact_id: ''
  });
  const [employeeContactsForManager, setEmployeeContactsForManager] = useState([]);
  const [skipReportingManager, setSkipReportingManager] = useState(false);

  useEffect(() => {
    if (!promoteDialog) return;
    (async () => {
      try {
        const r = await api.get('/contacts?contact_type=employee&status=active&limit=500');
        setEmployeeContactsForManager(r.data.data || []);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [promoteDialog]);

  const handlePromoteToEmployee = async () => {
    if (!isAdmin || !skipReportingManager) {
      if (!promoteForm.reporting_manager_contact_id) {
        toast.error('Select a reporting manager (another employee contact).');
        return;
      }
    }
    setPromoting(true);
    try {
      const payload = {
        department: promoteForm.department,
        designation: promoteForm.designation,
        employment_type: promoteForm.employment_type,
        create_user_account: promoteForm.create_user_account,
        temporary_password: promoteForm.temporary_password || undefined,
        reporting_manager_contact_id: promoteForm.reporting_manager_contact_id || undefined,
        skip_reporting_manager: isAdmin && skipReportingManager
      };
      const response = await api.post(`/contacts/${id}/promote-to-employee`, payload);
      toast.success('Contact promoted to employee successfully!');
      setPromoteDialog(false);
      fetchContact();
      // Navigate to the new employee profile
      if (response.data.data?.employee?.id) {
        navigate(`/employees/${response.data.data.employee.id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to promote to employee');
    } finally {
      setPromoting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!contact) {
    return null;
  }
  const workAuthLabel =
    workAuthOptions.find((x) => x.code === contact.work_authorization)?.label ||
    (contact.work_authorization ? String(contact.work_authorization).toUpperCase().replace(/_/g, ' ') : null);

  const currentTabKey = tabDefs[activeTab]?.key || 'overview';

  return (
    <Box>
      <DetailPageHeader
        title="Contact Details"
        onBack={embedded && typeof onCloseEmbedded === 'function' ? onCloseEmbedded : () => navigate('/contacts')}
        actions={
          <>
            {contactIsContractor(contact) && !contact.promoted_to_employee_at && isAdmin && (
              <Button
                variant="outlined"
                color="success"
                startIcon={<PromoteIcon />}
                onClick={() => setPromoteDialog(true)}
              >
                Promote to Employee
              </Button>
            )}
            {hasPermission('contacts_modify') && allowedNextRoles(effectiveContactRoles(contact)).length > 0 && (
              <Button
                variant="outlined"
                onClick={() => {
                  const next = allowedNextRoles(effectiveContactRoles(contact));
                  setAdvanceRole(next[0] || '');
                  setAdvanceDialog(true);
                }}
              >
                Advance role
              </Button>
            )}
            {hasPermission('contacts_modify') && (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/contacts/${id}/edit`)}
              >
                Edit
              </Button>
            )}
          </>
        }
      />

      <DetailTwoColumnGrid
        sidebar={
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Avatar
                sx={{
                  width: 100,
                  height: 100,
                  mx: 'auto',
                  mb: 2,
                  bgcolor: 'primary.main',
                  fontSize: '2rem'
                }}
              >
                {contact.first_name?.[0]}{contact.last_name?.[0]}
              </Avatar>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {contact.first_name} {contact.last_name}
              </Typography>
              {contact.contact_code && (
                <Chip
                  label={contact.contact_code}
                  size="small"
                  sx={{ mt: 1, fontFamily: 'monospace', fontWeight: 600 }}
                />
              )}
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="center" sx={{ mt: 1 }}>
                {effectiveContactRoles(contact).map((role) => (
                  <Chip
                    key={role}
                    label={role.replace(/_/g, ' ').toUpperCase()}
                    size="small"
                    sx={{
                      bgcolor: `${contactTypeColors[role] || '#6b7280'}20`,
                      color: contactTypeColors[role] || '#6b7280'
                    }}
                  />
                ))}
              </Stack>
              <Chip
                label={contact.status?.toUpperCase()}
                size="small"
                sx={{
                  mt: 1,
                  ml: 1,
                  bgcolor: contact.status === 'active' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(107, 114, 128, 0.15)',
                  color: contact.status === 'active' ? '#10b981' : '#6b7280'
                }}
              />

              <Divider sx={{ my: 3 }} />

              <List dense>
                <ListItem>
                  <EmailIcon sx={{ mr: 2, color: 'text.secondary' }} />
                  <ListItemText primary={contact.email} />
                </ListItem>
                {contact.phone && (
                  <ListItem>
                    <PhoneIcon sx={{ mr: 2, color: 'text.secondary' }} />
                    <ListItemText primary={contact.phone} />
                  </ListItem>
                )}
                {contact.city && (
                  <ListItem>
                    <LocationIcon sx={{ mr: 2, color: 'text.secondary' }} />
                    <ListItemText primary={`${contact.city}, ${contact.state}`} />
                  </ListItem>
                )}
                {contact.linkedin_url && (
                  <ListItem 
                    component="a" 
                    href={contact.linkedin_url} 
                    target="_blank"
                    sx={{ color: 'inherit', textDecoration: 'none' }}
                  >
                    <LinkedInIcon sx={{ mr: 2, color: 'text.secondary' }} />
                    <ListItemText primary="LinkedIn Profile" />
                  </ListItem>
                )}
              </List>
              <RecordAuditMeta
                creator={contact.creator}
                updater={contact.updater}
                created_at={contact.created_at}
                updated_at={contact.updated_at}
                createdAt={contact.createdAt}
                updatedAt={contact.updatedAt}
              />
            </CardContent>
          </Card>
        }
      >
          <Card>
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
            >
              {tabDefs.map((t) => (
                <Tab key={t.key} label={t.label} />
              ))}
            </Tabs>

            <CardContent>
              {currentTabKey === 'overview' && (
                <Grid container spacing={3}>
                  {contactHasRole(contact, 'candidate') && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Candidate Work Authorization
                      </Typography>
                      <Typography variant="body1">
                        {workAuthLabel || 'Not Specified'}
                      </Typography>
                    </Grid>
                  )}
                  {contactHasRole(contact, 'employee') && isAdmin && (
                    <>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Employee workplace (administrators)
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Reporting manager
                        </Typography>
                        {contact.reportingManagerContact ? (
                          <Chip
                            label={
                              [
                                contact.reportingManagerContact.first_name,
                                contact.reportingManagerContact.last_name,
                              ]
                                .filter(Boolean)
                                .join(' ') || contact.reportingManagerContact.email
                            }
                            onClick={() =>
                              navigate(`/contacts/${contact.reportingManagerContact.id}`)
                            }
                            sx={{ mt: 0.5, cursor: 'pointer' }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            None
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Alternate manager
                        </Typography>
                        {contact.alternateReportingManagerContact ? (
                          <Chip
                            label={
                              [
                                contact.alternateReportingManagerContact.first_name,
                                contact.alternateReportingManagerContact.last_name,
                              ]
                                .filter(Boolean)
                                .join(' ') || contact.alternateReportingManagerContact.email
                            }
                            onClick={() =>
                              navigate(`/contacts/${contact.alternateReportingManagerContact.id}`)
                            }
                            sx={{ mt: 0.5, cursor: 'pointer' }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            None
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Payroll / external employee ID
                        </Typography>
                        <Typography variant="body1">
                          {contact.external_employee_id?.trim()
                            ? contact.external_employee_id
                            : 'Not set'}
                        </Typography>
                      </Grid>
                    </>
                  )}
                  {contactHasRole(contact, 'employee') && !isAdmin && (
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ mt: 1 }}>
                        Reporting relationships and payroll identifiers for employees are visible only to
                        administrators, and are maintained under Administration → Employees.
                      </Alert>
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Experience
                    </Typography>
                    <Typography variant="body1">
                      {contact.experience_years ? `${contact.experience_years} years` : 'Not Specified'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Expected Salary
                    </Typography>
                    <Typography variant="body1">
                      {contact.expected_salary ? `$${contact.expected_salary.toLocaleString()}` : 'Not Specified'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Skills
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                      {contact.skills?.split(',').map((skill, i) => (
                        <Chip key={i} label={skill.trim()} size="small" />
                      )) || <Typography variant="body2" color="text.secondary">No skills listed</Typography>}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="body2">
                      {contact.notes || 'No notes'}
                    </Typography>
                  </Grid>
                </Grid>
              )}

              {currentTabKey === 'accounts' && (
                <Box>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Company accounts (clients, vendors) linked to this person.
                    </Typography>
                    {hasPermission('contacts_modify') && hasPermission('accounts_view') && (
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        variant="contained"
                        onClick={() => {
                          setAddAccountOpen(true);
                          setSelectedAccount(null);
                          setAccountSearch('');
                        }}
                      >
                        Add account
                      </Button>
                    )}
                  </Box>
                  {(!contact.Accounts || contact.Accounts.length === 0) && (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No company accounts linked yet. Use &quot;Add account&quot; to associate this contact with one or more
                      accounts.
                    </Typography>
                  )}
                  {contact.Accounts?.length > 0 && (
                    <List dense disablePadding>
                      {contact.Accounts.map((account) => (
                        <ListItem
                          key={account.id}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            mb: 1,
                            alignItems: 'flex-start'
                          }}
                          secondaryAction={
                            hasPermission('contacts_modify') ? (
                              <IconButton
                                edge="end"
                                size="small"
                                aria-label="Remove account link"
                                onClick={() => openDeleteChoice('account', account)}
                              >
                                <UnlinkIcon fontSize="small" />
                              </IconButton>
                            ) : null
                          }
                        >
                          <ListItemText
                            primary={
                              <Button
                                variant="text"
                                sx={{ p: 0, minWidth: 0, justifyContent: 'flex-start', fontWeight: 600 }}
                                onClick={() => navigate(`/accounts/${account.id}`)}
                              >
                                {account.company_name}
                              </Button>
                            }
                            secondary={
                              <>
                                <Typography component="span" variant="caption" display="block" color="text.secondary">
                                  {account.account_code}
                                  {account.account_type ? ` · ${String(account.account_type).replace(/_/g, ' ')}` : ''}
                                  {account.status ? ` · ${account.status}` : ''}
                                </Typography>
                                {account.contact_accounts?.relationship_type && (
                                  <Chip
                                    size="small"
                                    label={String(account.contact_accounts.relationship_type).replace(/_/g, ' ')}
                                    sx={{ mt: 0.5, height: 22, fontSize: '0.7rem' }}
                                  />
                                )}
                              </>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              )}

              {currentTabKey === 'submissions' && (
                <Box>
                  {contact.submissions?.length === 0 ? (
                    <Typography color="text.secondary" textAlign="center" py={4}>
                      No job submissions yet
                    </Typography>
                  ) : (
                    <List>
                      {contact.submissions?.map(submission => (
                        <ListItem key={submission.id} divider>
                          <ListItemText
                            primary={submission.JobRequirement?.title}
                            secondary={`Status: ${submission.status} • Submitted: ${new Date(submission.submitted_at).toLocaleDateString()}`}
                          />
                          <Chip label={submission.status} size="small" />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              )}

              {currentTabKey === 'whichBench' && (
                <Box>
                  {benchAssignmentsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : benchAssignments.length === 0 ? (
                    <Typography color="text.secondary" textAlign="center" py={4}>
                      This candidate is not on any bench yet.
                    </Typography>
                  ) : (
                    <List>
                      {benchAssignments.map((row) => (
                        <ListItem key={row.id} divider secondaryAction={
                          row.bench?.id ? (
                            <Button size="small" variant="outlined" onClick={() => navigate(`/bench-sales/${row.bench.id}`)}>
                              Open bench
                            </Button>
                          ) : null
                        }>
                          <ListItemText
                            primary={row.bench?.name || 'Bench'}
                            secondary={`${row.bench?.bench_code || ''} · ${row.availability_status || '—'} · Vis.: ${row.bench?.visibility || '—'}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              )}

              {currentTabKey === 'documents' && (
                <Box>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Documents for this contact (resumes, IDs, agreements, etc.). Uploads are stored in Documents.
                    </Typography>
                    {hasPermission('documents_add') && (
                      <Button
                        size="small"
                        startIcon={<UploadIcon />}
                        variant="contained"
                        onClick={() => {
                          setAddDocOpen(true);
                          setDocForm({ title: '', document_type: defaultDocumentTypeCode(documentTypes), file: null });
                        }}
                      >
                        Add document
                      </Button>
                    )}
                  </Box>
                  {contact.documents?.length === 0 ? (
                    <Typography color="text.secondary" textAlign="center" py={4}>
                      No documents yet. {hasPermission('documents_add') ? 'Use “Add document” to upload a file.' : ''}
                    </Typography>
                  ) : (
                    <List>
                      {contact.documents?.map((doc) => {
                        const created = doc.created_at || doc.createdAt;
                        return (
                          <ListItem key={doc.id} divider>
                            <ListItemText
                              primary={doc.title}
                              secondary={`${labelForDocumentType(doc.document_type, documentTypes)} • ${
                                created ? new Date(created).toLocaleString() : ''
                              }`}
                            />
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 1, flexShrink: 0 }}>
                              {hasPermission('documents_view') && (
                                <Button
                                  size="small"
                                  startIcon={<DownloadIcon />}
                                  onClick={() => handleDocumentDownload(doc)}
                                >
                                  Download
                                </Button>
                              )}
                              {hasPermission('documents_delete') && (
                                <IconButton
                                  size="small"
                                  aria-label="Remove document from contact"
                                  onClick={() => openDeleteChoice('document', doc)}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Stack>
                          </ListItem>
                        );
                      })}
                    </List>
                  )}
                </Box>
              )}

              {currentTabKey === 'training' && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Training assignments linked to this contact (legacy assignments by category).
                  </Typography>
                  {trainingLoading ? (
                    <CircularProgress />
                  ) : trainingAssignments.length === 0 ? (
                    <Typography color="text.secondary">No training assignments found.</Typography>
                  ) : (
                    <List dense>
                      {trainingAssignments.map((row) => (
                        <ListItem key={row.id} divider>
                          <ListItemText
                            primary={row.TrainingCategory?.name || 'Category'}
                            secondary={`Status: ${row.status || '—'} · Assigned: ${
                              row.assigned_at ? new Date(row.assigned_at).toLocaleDateString() : '—'
                            }`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              )}

              {currentTabKey === 'exams' && (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Course and standalone exam attempts are stored per <strong>user account</strong>. If this trainee has a
                    login, open <strong>Training → My training</strong> while signed in as them, or review attempts from
                    the training admin exam screens.
                  </Alert>
                  <Typography variant="body2" color="text.secondary">
                    Contact-level exam history is not attached in this release; use the training module for the linked user.
                  </Typography>
                </Box>
              )}

              {currentTabKey === 'employee' && (
                <Box>
                  {contact.Employee?.id ? (
                    <Stack spacing={2}>
                      <Typography variant="body1">
                        This contact has an HR employee record: <strong>{contact.Employee.employee_code}</strong> (
                        {contact.Employee.status})
                      </Typography>
                      {isAdmin ? (
                        <Button variant="contained" onClick={() => navigate(`/employees/${contact.Employee.id}`)}>
                          Open employee HR record
                        </Button>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          You do not have permission to open the employee record.
                        </Typography>
                      )}
                    </Stack>
                  ) : (
                    <Typography color="text.secondary">
                      No employee HR profile is linked yet. Create or link one under Administration → Employees.
                    </Typography>
                  )}
                </Box>
              )}

              {currentTabKey === 'okr' && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    OKRs are tracked in the OKR module (objectives, scoreboards, employee map). Use the links below to open
                    the tools where this employee appears when configured.
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {hasPermission('okr') && (
                      <>
                        <Button variant="outlined" onClick={() => navigate('/okr/employee-map')}>
                          OKR employee map
                        </Button>
                        <Button variant="outlined" onClick={() => navigate('/okr/objectives')}>
                          Objectives
                        </Button>
                        <Button variant="outlined" onClick={() => navigate('/okr/my')}>
                          My OKRs
                        </Button>
                      </>
                    )}
                  </Stack>
                  {!hasPermission('okr') && (
                    <Typography variant="body2" color="text.secondary">
                      You need OKR permissions to open these pages.
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
      </DetailTwoColumnGrid>

      {/* Promote to Employee Dialog */}
      <Dialog open={promoteDialog} onClose={() => setPromoteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmployeeIcon color="success" />
            Promote to Employee
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info">
              This will convert {contact.first_name} {contact.last_name} from Contractor to Employee.
              An employee record will be created and linked to this contact.
            </Alert>
            
            <TextField
              label="Department"
              fullWidth
              value={promoteForm.department}
              onChange={(e) => setPromoteForm({ ...promoteForm, department: e.target.value })}
              placeholder="e.g., Engineering, Sales, HR"
            />
            
            <TextField
              label="Designation / Title"
              fullWidth
              value={promoteForm.designation}
              onChange={(e) => setPromoteForm({ ...promoteForm, designation: e.target.value })}
              placeholder="e.g., Software Engineer, Manager"
            />
            
            <Divider sx={{ my: 1 }} />

            <Autocomplete
              options={employeeContactsForManager.filter((c) => c.id !== id)}
              getOptionLabel={(c) =>
                `${[c.first_name, c.last_name].filter(Boolean).join(' ')} (${c.email})`
              }
              value={
                employeeContactsForManager.find((c) => c.id === promoteForm.reporting_manager_contact_id) ||
                null
              }
              onChange={(_, v) =>
                setPromoteForm((prev) => ({
                  ...prev,
                  reporting_manager_contact_id: v?.id || ''
                }))
              }
              disabled={isAdmin && skipReportingManager}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Reporting manager"
                  required={!(isAdmin && skipReportingManager)}
                  helperText="Must be a contact with type Employee (with HR profile). Admins can use the option below for org root."
                />
              )}
            />

            {isAdmin && (
              <FormControlLabel
                control={
                  <Switch
                    checked={skipReportingManager}
                    onChange={(_, checked) => setSkipReportingManager(checked)}
                  />
                }
                label="No reporting manager (org root only)"
              />
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={promoteForm.create_user_account}
                  onChange={(e) => setPromoteForm({ ...promoteForm, create_user_account: e.target.checked })}
                />
              }
              label="Create login and assign Employees role (default)"
            />

            {promoteForm.create_user_account && (
              <TextField
                label="Temporary password (optional)"
                type="password"
                fullWidth
                value={promoteForm.temporary_password}
                onChange={(e) => setPromoteForm({ ...promoteForm, temporary_password: e.target.value })}
                helperText="Leave blank to use a generated password returned in the API response"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromoteDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="success"
            onClick={handlePromoteToEmployee}
            disabled={promoting}
            startIcon={promoting ? <CircularProgress size={20} /> : <PromoteIcon />}
          >
            {promoting ? 'Promoting...' : 'Promote to Employee'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={advanceDialog} onClose={() => setAdvanceDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Advance contact role</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info">
              Ladder: Contact → Candidate (Job Seeker) → Employee or Contractor (Independent / C2C).
              You can also add Trainer / Instructor / Teacher or Trainee.
            </Alert>
            <FormControl fullWidth>
              <InputLabel>Next role</InputLabel>
              <Select
                value={advanceRole}
                label="Next role"
                onChange={(e) => setAdvanceRole(e.target.value)}
              >
                {allowedNextRoles(effectiveContactRoles(contact)).map((code) => (
                  <MenuItem key={code} value={code}>
                    {TYPE_LABELS[code] || code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdvanceDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={advancing || !advanceRole}
            onClick={async () => {
              setAdvancing(true);
              try {
                await api.post(`/contacts/${id}/advance-role`, { role: advanceRole });
                toast.success(`Added role: ${TYPE_LABELS[advanceRole] || advanceRole}`);
                setAdvanceDialog(false);
                fetchContact();
              } catch (e) {
                toast.error(e.response?.data?.message || 'Failed to advance role');
              } finally {
                setAdvancing(false);
              }
            }}
          >
            {advancing ? 'Saving…' : 'Add role'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addAccountOpen}
        onClose={() => {
          setAddAccountOpen(false);
          setQuickNewAccountOpen(false);
          resetQuickNewAccountForm();
          setSelectedAccount(null);
          setAccountSearch('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add account to contact</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {canAddAccounts && (
              <Box>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<AddBusinessIcon />}
                  onClick={() => {
                    resetQuickNewAccountForm();
                    setQuickNewAccountOpen(true);
                  }}
                  fullWidth
                >
                  New account…
                </Button>
                <FormHelperText sx={{ mx: 0, mt: 0.5 }}>
                  Opens a second step on top of this dialog. After save, the new company appears in the search list and
                  is selected so you can link it without leaving this contact.
                </FormHelperText>
              </Box>
            )}
            <Autocomplete
              options={accountAutocompleteOptions}
              loading={false}
              filterOptions={(options, params) => {
                const createRow = options.find((o) => o.__createRow);
                const rest = options.filter((o) => !o.__createRow);
                const filteredRest = filterAccountPickOptions(rest, params);
                return createRow && canAddAccounts ? [createRow, ...filteredRest] : filteredRest;
              }}
              getOptionLabel={(a) => {
                if (!a) return '';
                if (a.__createRow) return String(a.company_name || 'Create new account…');
                return a.company_name ? `${a.company_name} (${a.account_code || a.id})` : '';
              }}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              value={selectedAccount}
              onChange={(_, v) => {
                if (v?.__createRow) {
                  resetQuickNewAccountForm();
                  setQuickNewAccountOpen(true);
                  return;
                }
                setSelectedAccount(v);
              }}
              onInputChange={(_, v) => setAccountSearch(v)}
              renderOption={(props, option) => {
                if (option.__createRow) {
                  return (
                    <li {...props} key={option.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, color: 'primary.main' }}>
                        <AddBusinessIcon fontSize="small" />
                        {option.company_name}
                      </Box>
                    </li>
                  );
                }
                return (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2">{option.company_name}</Typography>
                      {option.account_code ? (
                        <Typography variant="caption" color="text.secondary">
                          {option.account_code}
                        </Typography>
                      ) : null}
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search accounts"
                  placeholder="Type to search, or pick “Create new account…”"
                />
              )}
            />
            <FormControl fullWidth size="small">
              <InputLabel id="ca-rel-label">Relationship</InputLabel>
              <Select
                labelId="ca-rel-label"
                label="Relationship"
                value={newAccountRelationship}
                onChange={(e) => setNewAccountRelationship(e.target.value)}
              >
                <MenuItem value="primary">Primary</MenuItem>
                <MenuItem value="secondary">Secondary</MenuItem>
                <MenuItem value="billing">Billing</MenuItem>
                <MenuItem value="technical">Technical</MenuItem>
                <MenuItem value="hr">HR</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddAccountOpen(false);
              setQuickNewAccountOpen(false);
              resetQuickNewAccountForm();
              setSelectedAccount(null);
              setAccountSearch('');
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleLinkAccount} disabled={linkingAccount}>
            {linkingAccount ? 'Linking…' : 'Link account'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={quickNewAccountOpen}
        onClose={() => !creatingAccount && setQuickNewAccountOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={(theme) => ({ zIndex: theme.zIndex.modal + 1 })}
      >
        <DialogTitle>New account</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info" sx={{ py: 0.5 }}>
              Saves this company, adds it to the list above, and selects it so you can click <strong>Link account</strong>{' '}
              with the relationship you chose on this screen. You can add full address and billing details later from
              Accounts.
            </Alert>
            <TextField
              required
              label="Company name"
              fullWidth
              value={quickNewAccount.company_name}
              onChange={(e) => setQuickNewAccount((p) => ({ ...p, company_name: e.target.value }))}
              autoFocus
            />
            <FormControl fullWidth size="small">
              <InputLabel id="qna-type-label">Account type</InputLabel>
              <Select
                labelId="qna-type-label"
                label="Account type"
                value={quickNewAccount.account_type}
                onChange={(e) => setQuickNewAccount((p) => ({ ...p, account_type: e.target.value }))}
              >
                <MenuItem value="client">Client</MenuItem>
                <MenuItem value="vendor">Vendor</MenuItem>
                <MenuItem value="both">Both</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel id="qna-status-label">Status</InputLabel>
              <Select
                labelId="qna-status-label"
                label="Status"
                value={quickNewAccount.status}
                onChange={(e) => setQuickNewAccount((p) => ({ ...p, status: e.target.value }))}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="prospect">Prospect</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Industry (optional)"
              fullWidth
              value={quickNewAccount.industry}
              onChange={(e) => setQuickNewAccount((p) => ({ ...p, industry: e.target.value }))}
              placeholder="e.g. Information Technology"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !creatingAccount && setQuickNewAccountOpen(false)} disabled={creatingAccount}>
            Back
          </Button>
          <Button variant="contained" onClick={handleQuickCreateAccount} disabled={creatingAccount}>
            {creatingAccount ? 'Saving…' : 'Create & link'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={duplicateAccountDialog.open}
        onClose={() => setDuplicateAccountDialog({ open: false, existing: null, serverMessage: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Request submitted for review</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {duplicateAccountDialog.serverMessage}
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            The company name matches an existing account. Track approval under{' '}
            <strong>My Items → My account status</strong>.
          </Typography>
          {duplicateAccountDialog.existing ? (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {duplicateAccountDialog.existing.company_name}
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }} fontFamily="monospace">
                Reference: {duplicateAccountDialog.existing.account_code ?? '—'}
              </Typography>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateAccountDialog({ open: false, existing: null, serverMessage: '' })}>
            Close
          </Button>
          <Button variant="contained" onClick={() => navigate('/my-items/my-account-status')}>
            Open My account status
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addDocOpen} onClose={() => !docUploading && setAddDocOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add document</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
              {docForm.file ? docForm.file.name : 'Choose file'}
              <input
                type="file"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  setDocForm((prev) => ({
                    ...prev,
                    file: f || null,
                    title: prev.title || (f ? f.name.replace(/\.[^.]+$/, '') : prev.title)
                  }));
                }}
              />
            </Button>
            <TextField
              label="Title"
              fullWidth
              value={docForm.title}
              onChange={(e) => setDocForm((p) => ({ ...p, title: e.target.value }))}
              placeholder="Display name for this file"
            />
            <FormControl fullWidth size="small">
              <InputLabel id="cd-type-label">Document type</InputLabel>
              <Select
                labelId="cd-type-label"
                label="Document type"
                value={docForm.document_type}
                onChange={(e) => setDocForm((p) => ({ ...p, document_type: e.target.value }))}
                disabled={docTypesLoading && documentTypes.length === 0}
              >
                {documentTypes.map((t) => (
                  <MenuItem key={t.code} value={t.code}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDocOpen(false)} disabled={docUploading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAddDocument} disabled={docUploading}>
            {docUploading ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteChoiceDialog.open}
        onClose={() => setDeleteChoiceDialog({ open: false, targetType: null, target: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {deleteChoiceDialog.targetType === 'account' ? 'Remove account from contact' : 'Remove document from contact'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Choose how you want to remove{' '}
            <strong>
              {deleteChoiceDialog.targetType === 'account'
                ? deleteChoiceDialog.target?.company_name
                : deleteChoiceDialog.target?.title || deleteChoiceDialog.target?.file_name}
            </strong>
            .
          </Typography>
          <RadioGroup value={deleteChoiceMode} onChange={(e) => setDeleteChoiceMode(e.target.value)}>
            <FormControlLabel
              value="unlink"
              control={<Radio />}
              label="Only delete the link to this contact"
            />
            <FormControlLabel
              value="permanent"
              control={<Radio />}
              disabled={
                deleteChoiceDialog.targetType === 'account' && !hasPermission('accounts_delete')
              }
              label="Delete permanently (respects retention days from Administration settings)"
            />
          </RadioGroup>
          {deleteChoiceDialog.targetType === 'account' && !hasPermission('accounts_delete') ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              You can unlink accounts from this contact, but permanent account deletion requires account delete permission.
            </Alert>
          ) : null}
          <Alert severity="info" sx={{ mt: 2 }}>
            If retention days is greater than 0, permanent delete is marked now and purged later.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteChoiceDialog({ open: false, targetType: null, target: null })}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirmDeleteChoice}>
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContactDetails;
